import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  IconButton,
  Chip,
  Box,
  Tooltip,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Code as CodeIcon,
  RemoveCircleOutline as RemoveIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { ReactComponent as ToolCropIcon } from 'new/components/icons/fi-rr-tool-crop.svg';
import { SkeletonList, ToolCardSkeleton } from '../AIToolsSection';
import { NexsetSelector, SelectedNexset } from '../AIToolsSection/NexsetSelector';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { useConfirmDialog } from 'new/elements/ConfirmDialog';
import { extractApiError } from '../../utils/errorUtils';
import { getToolKindLabel, getToolDisplayName } from '../../utils/toolLabels';
import {
  aiApi,
  useGetToolsetToolsQuery,
  useRemoveToolsFromToolsetMutation,
  useAddToolsToToolsetMutation,
  useCreateToolFromNexsetMutation,
  useActivateToolMutation,
} from '../../state/aiApi';
import type { ApiToolsetTool, CreateToolFromNexsetResponse } from '../../state/aiApiTypes';
import styles from './ToolsetToolsDialog.module.scss';

interface ToolsetToolsDialogProps {
  open: boolean;
  onClose: () => void;
  toolsetId: number | null;
  toolsetName?: string;
  toolsetDescription?: string;
}

const ToolRow: React.FC<{
  tool: ApiToolsetTool;
  onRemove?: (toolId: number) => void;
  isRemoving?: boolean;
}> = ({ tool, onRemove, isRemoving }) => (
  <Box
    sx={{
      p: 1.5,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
      <Box
        sx={{
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          flexShrink: 0,
        }}
      >
        <ToolCropIcon className={styles.toolCropIcon} />
      </Box>
      <Box flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={500}>
            {getToolDisplayName(tool)}
          </Typography>
          {tool.tool_kind && (
            <Chip
              label={getToolKindLabel(tool.tool_kind)}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 12 }}
            />
          )}
          {tool.tool_status && (
            <Chip
              label={tool.tool_status}
              size="small"
              color={tool.tool_status === 'active' ? 'success' : 'default'}
              sx={{ height: 20, fontSize: 12 }}
            />
          )}
        </Stack>
        {tool.tool_description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {tool.tool_description}
          </Typography>
        )}
        {tool.tool_key && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
          >
            <CodeIcon sx={{ fontSize: 12, mr: 0.5 }} />
            {tool.tool_key}
          </Typography>
        )}
      </Box>
      {onRemove && (
        <Tooltip title="Remove from toolset">
          <IconButton
            size="small"
            onClick={() => onRemove(tool.tool_id)}
            disabled={isRemoving}
            sx={{ color: 'error.main', flexShrink: 0 }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  </Box>
);

export const ToolsetToolsDialog: React.FC<ToolsetToolsDialogProps> = ({
  open,
  onClose,
  toolsetId,
  toolsetName,
  toolsetDescription,
}) => {
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const {
    data: tools = [],
    isLoading,
    error,
  } = useGetToolsetToolsQuery(toolsetId!, {
    skip: !toolsetId || !open,
  });
  const [removeTools, { isLoading: isRemoving }] = useRemoveToolsFromToolsetMutation();
  const [addToolsToToolset] = useAddToolsToToolsetMutation();
  const [createToolFromNexset] = useCreateToolFromNexsetMutation();
  const [activateTool] = useActivateToolMutation();

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedNexsets, setSelectedNexsets] = useState<SelectedNexset[]>([]);
  const [isAdding, setIsAdding] = useState(false);

  // Reset add mode state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsAddMode(false);
      setSelectedNexsets([]);
      setIsAdding(false);
    }
  }, [open]);

  const handleRemoveTool = (toolId: number) => {
    if (!toolsetId) return;
    const tool = tools.find((t) => t.tool_id === toolId);
    confirm({
      title: 'Remove Tool',
      message: `Remove "${tool?.tool_name || `Tool ${toolId}`}" from this toolset?`,
      confirmText: 'Remove',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await removeTools({ toolsetId, data: { tool_ids: [toolId] } }).unwrap();
          showSuccess('Tool removed from toolset');
        } catch (err) {
          showError(extractApiError(err, 'Failed to remove tool'));
        }
      },
    });
  };

  const handleSelectionChange = useCallback((nexsets: SelectedNexset[]) => {
    setSelectedNexsets(nexsets);
  }, []);

  const handleAddTools = async () => {
    if (!toolsetId || selectedNexsets.length === 0) return;
    setIsAdding(true);
    try {
      // Step 1: Create tools from each selected nexset and activate them
      const results = await Promise.allSettled(
        selectedNexsets.map(async (nexset) => {
          const result = await createToolFromNexset({
            nexset_id: parseInt(nexset.id, 10),
            publish: true,
          }).unwrap();
          // Backend may return tool in "draft" — explicitly activate
          if (result.status === 'draft' || result.tool_status === 'draft') {
            try {
              await activateTool(result.tool_id).unwrap();
            } catch {
              /* proceed anyway */
            }
          }
          return result;
        }),
      );

      const succeeded = results.filter(
        (r): r is PromiseFulfilledResult<CreateToolFromNexsetResponse> => r.status === 'fulfilled',
      );
      const failed = results.filter((r) => r.status === 'rejected');

      if (succeeded.length === 0) {
        showError('Failed to create any tools from selected Nexsets');
        return;
      }

      // Step 2: Add the successfully created tools to the toolset
      const toolIds = succeeded.map((r) => r.value.tool_id);
      await addToolsToToolset({ toolsetId, data: { tool_ids: toolIds } }).unwrap();
      // Ensure the standalone Tools tab reflects newly created tools regardless
      // of whether activateTool was called (skipped for already-active tools)
      dispatch(aiApi.util.invalidateTags(['ToolList']));

      if (failed.length > 0) {
        showSuccess(
          `${succeeded.length} tool${succeeded.length !== 1 ? 's' : ''} added, ${failed.length} failed`,
        );
      } else {
        showSuccess(
          `${succeeded.length} tool${succeeded.length !== 1 ? 's' : ''} added to toolset`,
        );
      }
      setSelectedNexsets([]);
      setIsAddMode(false);
    } catch (err) {
      showError(extractApiError(err, 'Failed to add tools'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleExitAddMode = () => {
    setIsAddMode(false);
    setSelectedNexsets([]);
  };

  const errorMessage = error
    ? 'data' in error
      ? (error.data as any)?.message
      : 'Failed to load tools'
    : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '80vh', maxHeight: 800 } }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            {isAddMode ? (
              <Tooltip title="Back to tools list">
                <IconButton onClick={handleExitAddMode} size="small" disabled={isAdding}>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <div className={styles.dialogIcon}>
                <ToolCropIcon />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <Typography variant="h6">
                {isAddMode ? 'Add Tools' : toolsetName || 'Toolset Tools'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAddMode
                  ? 'Select Nexsets to add as tools to this toolset'
                  : toolsetDescription || 'Manage tools in this toolset'}
              </Typography>
            </div>
            <Stack direction="row" alignItems="center" spacing={1} className={styles.headerActions}>
              {!isAddMode && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddMode(true)}
                >
                  Add Tools
                </Button>
              )}
              <IconButton onClick={onClose} size="small" disabled={isAdding}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {isAddMode ? (
            <div className={styles.nexsetSelectorContainer}>
              <NexsetSelector
                selectedNexsets={selectedNexsets}
                onSelectionChange={handleSelectionChange}
                multiSelect
                height={400}
              />
            </div>
          ) : isLoading ? (
            <SkeletonList count={5} component={ToolCardSkeleton} spacing={1} />
          ) : errorMessage ? (
            <Stack alignItems="center" justifyContent="center" py={8}>
              <Typography variant="body2" color="error">
                {errorMessage}
              </Typography>
            </Stack>
          ) : tools.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" py={8} spacing={2}>
              <Typography variant="body2" color="text.secondary">
                No tools found in this toolset
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setIsAddMode(true)}>
                Add Tools from Nexsets
              </Button>
            </Stack>
          ) : (
            <Box className={styles.toolsContainer}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {tools.length} tool{tools.length !== 1 ? 's' : ''} in this toolset
              </Typography>
              <Stack spacing={1}>
                {tools.map((tool) => (
                  <ToolRow
                    key={tool.tool_id}
                    tool={tool}
                    onRemove={handleRemoveTool}
                    isRemoving={isRemoving}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>

        {isAddMode && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleExitAddMode} disabled={isAdding}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddTools}
              disabled={isAdding || selectedNexsets.length === 0}
              startIcon={isAdding ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {isAdding
                ? 'Adding...'
                : `Add ${selectedNexsets.length} Tool${selectedNexsets.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      {ConfirmDialogComponent}
    </>
  );
};
